import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isArray } from 'tone';

import PageController from './common/pageController';
import DepartmentSelection from './InviteTeamMembers/DepartmentSelection';
import EmailTextField from './InviteTeamMembers/emailAddressTextField';
import FullNameTextField from './InviteTeamMembers/fullNameTextField';
import HeaderText from './InviteTeamMembers/headerText';

import { axiosInstanceForTenantAPI } from '../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../assets/utils/helpers';
import { useAuth } from '../../../contexts/auth/authContext';
import ErrorType from '../../../interfaces/ErrorType';

const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

interface QuickstartInviteTeamMembersProps {
  hasTeamMembers: boolean;
  screenNum: number;
  maxScreenNum: number;
  changeScreen: (num: number) => void;
}

interface InviteTeamMembersProps {
  name: string;
  email: string;
  department: string;
}

interface DepartmentDropdownObject {
  id: number;
  name: string;
}

const roundedGradientButton = {
  borderRadius: '100px',
  width: '164px',
  color: 'black',
  fontWeight: 500,
  fontSize: '20px',
  textTransform: 'none',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '100px',
    padding: '2px', //ajust the padding to change the width of the border radius
    background: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
  '&:hover': {
    backgroundImage: 'linear-gradient(270deg, #00CECF 0%, #2449FC 100%)',
    color: 'white',
  },
};

const QuickstartInviteTeamMembers = ({
  hasTeamMembers,
  screenNum,
  maxScreenNum,
  changeScreen,
}: QuickstartInviteTeamMembersProps) => {
  const { slug } = useAuth();
  const intl = useIntl();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [isInvitationDone, setIsInvitationDone] = useState<boolean>(false);
  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const [requestStatus, setRequestStatus] = useState({
    isLoading: false,
    success: false,
    error: false,
    text: '',
  });
  const [departmentList, setDepartmentList] = useState<
  DepartmentDropdownObject[]
  >([]);
  const [invitedUsers, setInvitedUsers] = useState<InviteTeamMembersProps[]>([
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
  ]);

  const [invitedUsersFieldErrors, setInvitedUsersFieldErrors] = useState<
  InviteTeamMembersProps[]
  >([
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
    {
      name: '',
      email: '',
      department: '',
    },
  ]);

  const handleErrorMessages = (err: unknown) => {
    let errorMessage = intl.formatMessage({
      defaultMessage: 'Error happened. We\'re currently fixing it.',
    });

     if (isRESTAPIError(err)) {
       if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
         errorMessage = intl.formatMessage({
           defaultMessage: 'Department not found on your account',
         });
       } else if (
         `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
       ) {
         errorMessage = intl.formatMessage({
           defaultMessage: 'Error happened in the server',
         });
       } else if (
         `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
       ) {
         const data = err.response.data;
         if (isArray(data) && data.length > 0) {
           data.map(({ email, full_name, department }, key) => {
             if (email && isArray(email)) {
               setFieldErrorHandler(key, 'email', email[0]);
             }
             if (full_name && isArray(full_name)) {
               setFieldErrorHandler(key, 'email', full_name[0]);
             }
             if (department && isArray(department)) {
               setFieldErrorHandler(key, 'department', department[0]);
             }
           });
         }
       }
     }

     return errorMessage;
  };

  const fetchDepartmentDropdownHandler = useCallback(async () => {
    setIsLoading(true);
    setError({
      errorType: false,
      message: '',
    });
    try {
      const response = await axiosInstanceForTenantAPI(companySlug).get(
        '/department',
      );
      const data = response.data;
      setDepartmentList(data);
      setIsLoading(false);
    } catch (err: unknown) {
      let errorMessage = intl.formatMessage({
        defaultMessage: 'Error happened. We\'re currently fixing it.',
      });
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Department not found on your account',
          });
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          errorMessage = intl.formatMessage({
            defaultMessage: 'Error happened in the server',
          });
        }
      }
      setError({
        errorType: true,
        message: errorMessage,
      });
      setIsLoading(false);
    }
  }, [companySlug, intl]);

  useEffect(() => {
    fetchDepartmentDropdownHandler();
  }, [fetchDepartmentDropdownHandler]);

  const setNameHandler = (index: number, value: string) => {
    const newInvitedUsers = [...invitedUsers];
    newInvitedUsers[index].name = value;
    setInvitedUsers(newInvitedUsers);
  };

  const setEmailHandler = (index: number, value: string) => {
    const newInvitedUsers = [...invitedUsers];
    newInvitedUsers[index].email = value;
    setInvitedUsers(newInvitedUsers);
  };

  const setDepartmentHandler = (index: number, value: string) => {
    const newInvitedUsers = [...invitedUsers];
    newInvitedUsers[index].department = value;
    setInvitedUsers(newInvitedUsers);
  };

  const setFieldErrorHandler = (
    index: number,
    field: string,
    value: string,
  ) => {
    const newFieldErrors = [...invitedUsersFieldErrors];
    if (field === 'name') newFieldErrors[index].name = value;
    if (field === 'department') newFieldErrors[index].department = value;
    if (field === 'email') newFieldErrors[index].email = value;
    setInvitedUsersFieldErrors(newFieldErrors);
  };

  const isFieldValidated = () => {
    let validated = true;
    invitedUsers?.map((item, key) => {
      if (
        item?.name.length > 0 ||
        item?.email.length > 0 ||
        item?.department.length > 0
      ) {
        if (item.name.length <= 0) {
          setFieldErrorHandler(
            key,
            'name',
            intl.formatMessage({ defaultMessage: 'Name is required' }),
          );
          validated = false;
        } else setFieldErrorHandler(key, 'name', '');
        if (item.email.length <= 0) {
          setFieldErrorHandler(
            key,
            'email',
            intl.formatMessage({ defaultMessage: 'Email Address is required' }),
          );
          validated = false;
        } else setFieldErrorHandler(key, 'email', '');
        if (!EMAIL_REGEX.test(item.email)) {
          setFieldErrorHandler(
            key,
            'email',
            intl.formatMessage({
              defaultMessage: 'Email Address must be valid',
            }),
          );
          validated = false;
        } else setFieldErrorHandler(key, 'email', '');
        if (item.department.length <= 0) {
          setFieldErrorHandler(
            key,
            'department',
            intl.formatMessage({ defaultMessage: 'Department is required' }),
          );
          validated = false;
        } else setFieldErrorHandler(key, 'department', '');
      }
    });
    return validated;
  };

  const InviteTeamMembersHandler = async () => {
    setIsInvitationDone(false);
    try {
      setRequestStatus({
        ...requestStatus,
        isLoading: true,
      });
      const reqData = invitedUsers
        .filter(
          ({ name, email, department }) =>
            name?.length > 0 || email?.length > 0 || department?.length > 0,
        )
        .map(({ name, email, department }) => ({
          email,
          department,
          full_name: name,
          user_type: 3,
        }));
      const response = await axiosInstanceForTenantAPI(companySlug).post(
        '/invite-user',
        reqData,
      );

      const resStatus = response.status;
      if (resStatus === 201) {
        setRequestStatus({
          ...requestStatus,
          isLoading: false,
          success: true,
        });
        if (reqData?.length > 0) setIsInvitationDone(true);
      }

      return true;
    } catch (err: unknown) {
      const errorMessage = handleErrorMessages(err);
     
      setRequestStatus({
        isLoading: false,
        success: false,
        error: true,
        text: errorMessage,
      });
      return false;
    }
  };

  const setPage = async (pageNum: number) => {
    changeScreen(pageNum);
  };

  const inviteUserhandler = async () => {
    if (hasTeamMembers || isInvitationDone) changeScreen(screenNum);
    if (!isFieldValidated()) return;
    if (await InviteTeamMembersHandler()) changeScreen(screenNum + 1);
  };

  return (
    <Stack
      sx={{
        padding: '90px 64px',
        minHeight: '100vh',
        scrollSnapAlign: 'center',
      }}
    >
      <PageController
        page={screenNum}
        setPage={setPage}
        totalPage={maxScreenNum}
      />

      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk, sans-serif',
          fontSize: '28px',
          fontWeight: 700,
          color: '#181818',
          lineHeight: '150%',
          maxWidth: 650,
          marginBottom: '16px',
        }}
      >
        <FormattedMessage defaultMessage="Let's connect your team! Invite your first team members." />
      </Typography>
      <Stack gap={2} marginBottom={6}>
        <HeaderText />
        {Array.isArray(invitedUsers) &&
          invitedUsers.map((invitedUser, key) => (
            <Stack direction='row' gap={1} key={key}>
              <Stack flexBasis='50%'>
                <FullNameTextField
                  value={invitedUser.name}
                  setValue={(e) => setNameHandler(key, e)}
                  error={invitedUsersFieldErrors[key].name}
                />
              </Stack>
              <Stack flexBasis='50%'>
                <EmailTextField
                  value={invitedUser.email}
                  setValue={(e) => setEmailHandler(key, e)}
                  error={invitedUsersFieldErrors[key].email}
                />
              </Stack>
              <Stack flexBasis='25%'>
                <DepartmentSelection
                  isLoading={isLoading}
                  error={error}
                  list={departmentList}
                  value={invitedUser.department}
                  setValue={(e) => setDepartmentHandler(key, e)}
                  fielderror={invitedUsersFieldErrors[key].department}
                />
              </Stack>
            </Stack>
          ))}
      </Stack>
      <Button sx={roundedGradientButton} onClick={inviteUserhandler}>
        {requestStatus.isLoading ? (
          <CircularProgress size={20} sx={{ marginY: 1 }} />
        ) : (
          intl.formatMessage({ defaultMessage: 'Proceed' })
        )}
      </Button>
    </Stack>
  );
};

export default QuickstartInviteTeamMembers;
